<template>
  <div class="diceRoll">
    <template v-if="roll.length === 0">
      <div class="rollPlaceholder">
        <Dice die="1d4" :empty="true" />
        <Dice die="1d6" :empty="true" />
        <Dice die="1d8" :empty="true" />
        <Dice die="1d10" :empty="true" />
        <Dice die="1d12" :empty="true" />
        <Dice die="1d20" :empty="true" />
      </div>
    </template>
    <template v-else-if="outcome">
      <template v-for="(roll, i) in outcome.rolls" :key="i">
        <Dice v-if="roll.roll" :die="roll.roll" :roll="Math.abs(roll.outcome)" class="dice" :data-subtract="(roll.outcome < 0) ? true : null" />
        <span v-else :data-subtract="(roll.outcome < 0) ? true : null">{{ roll.display }}</span>
      </template>
      <span :data-equals="true">{{ outcome.outcome }}</span>
    </template>
    <template v-else>
      <template v-for="(part, i) in rollParts" :key="i">
        <Dice v-if="part.type === 'dice'" :die="part.display" class="dice" :data-subtract="part.subtract" />
        <span v-else :data-subtract="part.subtract">{{ part.display }}</span>
      </template>
    </template>
  </div>
</template>

<script>
import { defineComponent } from 'vue'

import rollDice from '@/helpers/rollDice'
import { useJoin, JOINED } from '@/hooks/room'

import Dice from '@/components/Dice'

export default defineComponent({
  props: {
    roll: {
      type: Array,
      required: true
    },

    preRoll: {
      type: Boolean
    },

    character: {
      type: Object
    },

    computed: {
      type: Object
    }
  },

  components: {
    Dice
  },

  data () {
    return {
      outcome: null,
      joinState: null
    }
  },

  created () {
    const { state: joinState, roll: sendRoll } = useJoin()
    this.joinState = joinState
    this.sendRoll = sendRoll
  },

  methods: {
    reset () {
      this.outcome = null
    },

    rollDice () {
      if (!this.roll.length) return
      this.outcome = rollDice(this.rollParts)
      if (this.joinState.status === JOINED) this.sendRoll(this.outcome)
      return this.outcome
    }
  },

  mounted () {
    if (this.preRoll) this.rollDice()
  },

  watch: {
    roll () {
      if (this.preRoll) this.rollDice()
    }
  },

  computed: {
    rollParts () {
      const props = this.$props
      return props.roll.reduce((acc, value) => {
        value = Number(value) || value
        let display = value
        
        let type = 'stat'
        let subtract = false
        if (typeof value === 'number') {
          type = 'number'
          subtract = value < 0
        } else {
          const op = value.charAt(0)
          subtract = op === '-'

          value = value.slice(1)
          switch (value) {
            case 'str':
            case 'dex':
            case 'con':
            case 'int':
            case 'wis':
            case 'cha':
              display = `${value} (${props.computed.abilities.value[value]})`
              value = props.computed.abilities.value[value]
              break
            case 'prof':
              display = `Prof. (${props.computed.difficulty.value.profBonus})`
              value = props.computed.difficulty.value.profBonus
              break
            case 'lvl':
              value = props.computed.difficulty.value.level
              display = value
              break
            default:
              type = 'dice'
          }
        }

        acc.push({
          value,
          display,
          type,
          subtract
        })

        return acc
      }, [])
    }
  }
})
</script>

<style lang="sass" scoped>
.diceRoll
  display: flex
  align-items: center
  justify-content: center
  height: 4.4em
  
  > span:not(.dice)
    color: #000
    font-size: 1em
    font-weight: bold
    display: inline-flex
    align-items: center
    text-transform: uppercase
    > em
      font-style: normal
      font-size: 200%

  > span
     &:not(:first-child):not(:only-child)
      &::before
        content: '+'
        display: inline-flex
        justify-content: center
        width: 2.4rem
        text-align: center
        font-size: 200%
        font-weight: bold
        // font-family: 'Playfair Display', sans-serif
        transform: translateY(-2px)
        color: #000
        opacity: 0.4
      &[data-subtract='true']::before
        content: '-'
      &[data-equals='true']
        font-size: 300%
        &::before
          font-size: 75%
          content: '='

  > .dice
    &:not(:first-child)
      margin-left: 2.4rem
      &::before
        position: absolute
        right: 100%

.rollPlaceholder
  display: flex
  align-items: center
  justify-content: center
  position: relative
  > *
    transform: scale(0.5)
    margin: 0 -0.6rem
    opacity: 0.87
</style>
